import React from 'react';
import { Bot, Loader2, User } from 'lucide-react';
import SafeHtml from '../common/SafeHtml';

/**
 * Render a single markdown-flavoured assistant response.
 * The output passes through DOMPurify (via <SafeHtml>) before hitting the DOM.
 */
const FormattedAssistant = ({ content }) => {
  const formatted = content
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    .replace(/`(.*?)`/g, '<code class="bg-slate-700 px-1 rounded text-cyan-400">$1</code>')
    .replace(/\n/g, '<br />');
  return <SafeHtml as="span" html={formatted} />;
};

const MessageRow = ({ msg }) => (
  <div className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
    {msg.role === 'assistant' && (
      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-cyan-500 to-purple-600 flex items-center justify-center flex-shrink-0">
        <Bot className="w-4 h-4 text-white" />
      </div>
    )}
    <div
      className={`max-w-[80%] rounded-2xl px-4 py-3 ${
        msg.role === 'user'
          ? 'bg-cyan-500 text-white rounded-br-md'
          : msg.isError
          ? 'bg-red-500/20 border border-red-500/30 text-red-300 rounded-bl-md'
          : 'bg-slate-800 border border-slate-700 text-gray-200 rounded-bl-md'
      }`}
    >
      <div className="text-sm leading-relaxed">
        {msg.role === 'assistant' ? <FormattedAssistant content={msg.content} /> : msg.content}
      </div>
    </div>
    {msg.role === 'user' && (
      <div className="w-8 h-8 rounded-lg bg-slate-700 flex items-center justify-center flex-shrink-0">
        <User className="w-4 h-4 text-gray-300" />
      </div>
    )}
  </div>
);

const LoadingBubble = () => (
  <div className="flex gap-3 justify-start">
    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-cyan-500 to-purple-600 flex items-center justify-center flex-shrink-0">
      <Bot className="w-4 h-4 text-white" />
    </div>
    <div className="bg-slate-800 border border-slate-700 rounded-2xl rounded-bl-md px-4 py-3">
      <div className="flex items-center gap-2">
        <Loader2 className="w-4 h-4 text-cyan-400 animate-spin" />
        <span className="text-gray-400 text-sm">Thinking...</span>
      </div>
    </div>
  </div>
);

const QuickSuggestions = ({ suggestions, onSend }) => (
  <div className="flex flex-wrap gap-2 mt-2">
    {suggestions.map((s, idx) => (
      <button
        key={`quick-${s}-${idx}`}
        data-testid={`copilot-quick-suggestion-${idx}`}
        onClick={() => onSend(s)}
        className="px-3 py-1.5 bg-slate-800 border border-slate-700 rounded-full text-xs text-gray-300 hover:text-cyan-400 hover:border-cyan-500/50 transition"
      >
        {s}
      </button>
    ))}
  </div>
);

/**
 * Scrollable message thread + loading bubble + quick-reply chip row.
 */
const CopilotMessages = ({ messages, isLoading, suggestions, onSend, scrollRef }) => (
  <>
    {messages.map((msg) => (
      <MessageRow key={msg.id} msg={msg} />
    ))}
    {isLoading && <LoadingBubble />}
    {suggestions.length > 0 && !isLoading && (
      <QuickSuggestions suggestions={suggestions} onSend={onSend} />
    )}
    <div ref={scrollRef} />
  </>
);

export default CopilotMessages;
